import apiV2 from '../api/v2';
import { UserFacingError } from '../utils/userFacingError';

// Transport for a PO's two file ids. Limits mirror the server's, so a bad
// pick is refused in the picker rather than after the upload.

export const ALLOWED_INVOICE_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/webp',
    'text/csv',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
];
export const MAX_INVOICE_BYTES = 25 * 1024 * 1024;
export const MAX_INVOICE_MB = 25;
export const ALLOWED_INVOICE_LABEL = 'PDF, JPEG, PNG, WebP, CSV or Excel';
// Extensions ride along with the MIME types: browsers report CSV and Excel
// picks inconsistently, so the `accept` filter needs both to be usable.
export const INVOICE_ACCEPT_ATTRIBUTE = [
    ...ALLOWED_INVOICE_TYPES,
    '.pdf',
    '.jpg',
    '.jpeg',
    '.png',
    '.webp',
    '.csv',
    '.xls',
    '.xlsx'
].join(',');

export interface PurchaseOrderFileUpload {
    fileId: string;
    originalFilename: string;
    mimeType: string;
    sizeBytes: number;
}

export interface PurchaseOrderFileUrl {
    url: string;
    expiresAt: string;
}

// null = acceptable.
export function invoiceFileRejectionReason(file: File): string | null {
    if (!ALLOWED_INVOICE_TYPES.includes(file.type)) {
        return `Invoice must be a ${ALLOWED_INVOICE_LABEL} file.`;
    }
    if (file.size > MAX_INVOICE_BYTES) {
        return `Invoice exceeds the ${MAX_INVOICE_MB}MB size limit.`;
    }
    return null;
}

export async function uploadVendorInvoiceFile(
    purchaseOrderId: string,
    file: File
): Promise<PurchaseOrderFileUpload> {
    const rejection = invoiceFileRejectionReason(file);
    if (rejection) throw new UserFacingError(rejection);

    const form = new FormData();
    form.append('file', file, file.name);

    const response = await apiV2.post<{ success: boolean; data: PurchaseOrderFileUpload }>(
        `/billing/purchase-orders/${purchaseOrderId}/files`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data;
}

export async function fetchPurchaseOrderFileUrl(
    purchaseOrderId: string,
    fileId: string
): Promise<PurchaseOrderFileUrl> {
    const response = await apiV2.get<{ success: boolean; data: PurchaseOrderFileUrl }>(
        `/billing/purchase-orders/${purchaseOrderId}/files/${encodeURIComponent(fileId)}/download-url`
    );
    return response.data.data;
}
